/**
 * What a card that asks shows and sends, DOM-free (as the phone's Home does): the answers split into the buttons
 * that fit in a row and the ones behind a menu, the record an answer goes to, and a refusal in plain words that names
 * who may answer. The words of the question are the card's own; the answers are the feed model's (`recordAsk`).
 */
import type { FeedAct, FeedItem } from '../../sharedHome.js';

/** A record answer: the one kind of act Home answers in place (Open, install and message acts are other paths). */
export type RecordAct = Extract<FeedAct, { kind: 'record' }>;

/** At most this many controls in the answer row: when there are more, the first ones stand and the rest go in a menu. */
export const ASK_ROW_MAX = 3;

export function recordActs(item: Pick<FeedItem, 'ask'>): RecordAct[] {
  return (item.ask?.acts ?? []).filter((a): a is RecordAct => a.kind === 'record');
}

/** The buttons in the row, and the answers left for the menu (the menu itself takes the last slot). */
export function splitActs(acts: RecordAct[]): { row: RecordAct[]; more: RecordAct[] } {
  if (acts.length <= ASK_ROW_MAX) return { row: acts, more: [] };
  return { row: acts.slice(0, ASK_ROW_MAX - 1), more: acts.slice(ASK_ROW_MAX - 1) };
}

/** `<projectId>/<notificationId>` into its two halves; undefined for a malformed id. */
export function splitRecordId(recordId: string): { projectId: string; id: string } | undefined {
  const at = recordId.indexOf('/');
  if (at <= 0 || at === recordId.length - 1) return undefined;
  return { projectId: recordId.slice(0, at), id: recordId.slice(at + 1) };
}

/** Names as a sentence: "Ana", "Ana and Dan", "Ana, Dan and Ben". */
export function namesWords(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Refusals the platform sends as a code alone, in the person's words. */
const CODE_WORDS: Record<string, string> = {
  already_done: 'This was already answered, by you or by someone else. Home is catching up.',
  version_conflict: 'This changed while you were looking at it. Home is catching up.',
  unknown_action: 'That answer is no longer on offer. Open the question to see what it asks now.',
};

/** A refusal that means the card is out of date: Home is read again so it settles. */
export const isStale = (code?: string): boolean => code === 'already_done' || code === 'version_conflict' || code === 'unknown_action';

/**
 * A refusal in words. The platform's own sentence leads; a Team Work refusal that names who was asked (`who`) gets
 * them named when the sentence does not already.
 */
export function refusalWords(message: string, body: unknown, code?: string): string {
  const said = CODE_WORDS[code ?? ''];
  if (said) return said;
  const who = (body as { who?: unknown } | undefined)?.who;
  const names = Array.isArray(who)
    ? who.map((w) => (w && typeof w === 'object' && typeof (w as { name?: unknown }).name === 'string' ? (w as { name: string }).name : '')).filter(Boolean)
    : [];
  if (names.length === 0 || names.some((n) => message.includes(n))) return message;
  return `${message.replace(/\s+$/, '')} ${names.length === 1 ? `${names[0]} was asked.` : `${namesWords(names)} were asked.`}`;
}
